import axios from "axios"
import { API_BASE_URL } from "../config/api"
import { requestLocaleHeaders, translate, translateError } from "../i18n"
import { clearAuthToken, getAuthToken } from "../utils/auth"
import { normalizeResponseMediaUrls } from "../utils/media"
import notice from "../utils/notice"

let requestVisibilityEpoch = 0

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") requestVisibilityEpoch += 1
  })
}

function isBackgroundTransportInterruption(error) {
  if (!error || error.response || typeof document === "undefined") return false
  if (axios.isCancel(error) || error.code === "ERR_CANCELED") return true

  const config = error.config || {}
  return Boolean(
    config.starliveStartedHidden ||
    document.hidden ||
    Number(config.starliveVisibilityEpoch) !== requestVisibilityEpoch
  )
}

function errorMessage(payload, fallback = "") {
  const response = payload && typeof payload === "object" ? payload : {}
  const detail = response.error_detail || response.detail || response.message || ""
  if (String(detail).trim()) return String(detail).trim()

  const code = response.error_code || response.message_code || response.msg
  if (code) {
    const key = `errors.${code}`
    const localized = translateError(code)
    if (localized && localized !== key) return localized
  }

  return fallback || translate("common.requestFailed")
}

const configuredTimeout = Number(process.env.VUE_APP_API_TIMEOUT)
// 跨区域数据库在本地开发时可能出现较高延迟；保留环境变量覆盖能力，生产环境可按部署距离收紧。
const requestTimeout = Number.isFinite(configuredTimeout) && configuredTimeout >= 1000
  ? configuredTimeout
  : 30000

const request = axios.create({
  baseURL: API_BASE_URL,
  timeout: requestTimeout,
  headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
  transformRequest: [data => {
    if (!data) return data
    // 上传接口使用 FormData；保留浏览器生成的 multipart boundary。
    if (typeof FormData !== "undefined" && data instanceof FormData) return data

    const formData = new URLSearchParams()
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })
    return formData
  }]
})

request.interceptors.request.use(config => {
  config.starliveVisibilityEpoch = requestVisibilityEpoch
  config.starliveStartedHidden = typeof document !== "undefined" && document.hidden

  // FormData 的 Content-Type 必须由浏览器生成，才能携带正确的 multipart boundary。
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type")
    } else if (config.headers) {
      delete config.headers["Content-Type"]
    }
  }

  const token = getAuthToken()
  Object.assign(config.headers, requestLocaleHeaders())
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

request.interceptors.response.use(response => {
  // 受保护媒体通过同一鉴权实例读取 Blob，不走 JSON 业务码解析。
  if (response.config && response.config.responseType === "blob") return response.data
  // 历史 `/uploads/...` 属于 ThinkPHP 公共媒体，不属于 H5 静态站点。
  const res = normalizeResponseMediaUrls(response.data)
  if (res.code !== 200) {
    const message = errorMessage(res)
    const error = new Error(message)
    error.responseData = res

    if (res.code === 401 && !response.config.skipAuthRedirect) {
      clearAuthToken()
      window.dispatchEvent(new CustomEvent("auth-required", { detail: res }))
    } else if (res.code === 403 && res.data && res.data.account_status) {
      clearAuthToken()
      window.dispatchEvent(new CustomEvent("account-restricted", { detail: res }))
    } else if (!response.config.silentError) {
      notice.error(message)
    }
    return Promise.reject(error)
  }

  return res
}, error => {
  const responseData = error.response && error.response.data
  const status = Number((responseData && responseData.code) || (error.response && error.response.status) || 0)
  const backgroundInterrupted = isBackgroundTransportInterruption(error)
  const fallbackMessage = error.code === "ECONNABORTED"
    ? translate("common.requestTimeout")
    : translate("common.networkError")
  const message = responseData
    ? errorMessage(responseData, fallbackMessage)
    : fallbackMessage

  if (status === 401 && !(error.config && error.config.skipAuthRedirect)) {
    clearAuthToken()
    window.dispatchEvent(new CustomEvent("auth-required", { detail: responseData || {} }))
  } else if (!backgroundInterrupted && (!error.config || !error.config.silentError)) {
    notice.error(message)
  }
  error.responseData = responseData || error.responseData
  return Promise.reject(error)
})

export default request
